import { FC, useEffect, useState, useMemo } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { useAllSubscriptionPlans } from '@hooks/api/subscription';
import { Organization } from '@models/organization.model';
import { OrganizationSubscriptionStatus } from '@models/subscription';
import { useTranslation } from '@hooks/useTranslation';
import SubscriptionStatusBadge from './SubscriptionStatusBadge';

interface AssignPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization: Organization | null;
  onAssign: (planId: string, billingPeriod: string) => void;
  isLoading: boolean;
  /** Opens the Extend free period modal for the same organization (TRIAL_EXPIRED note). */
  onExtendFreePeriod?: (organization: Organization) => void;
}

const AssignPlanModal: FC<AssignPlanModalProps> = ({
  isOpen,
  onClose,
  organization,
  onAssign,
  isLoading,
  onExtendFreePeriod,
}) => {
  const t = useTranslation('organizations');
  const billingPeriodOptions: SelectOption[] = useMemo(
    () => [
      { value: 'MONTHLY', label: t.billingPeriodMonthly },
      { value: 'ANNUAL', label: t.billingPeriodAnnual },
    ],
    [t.billingPeriodMonthly, t.billingPeriodAnnual],
  );
  const { data: plans, isLoading: isLoadingPlans } = useAllSubscriptionPlans({
    enabled: isOpen,
  });

  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [billingPeriod, setBillingPeriod] = useState<string>('MONTHLY');

  // Filter to show only BASE plans (not addons)
  const basePlans = useMemo(() => {
    return plans?.filter((plan) => !plan.isAddOn) || [];
  }, [plans]);

  const planOptions: SelectOption[] = useMemo(() => {
    return basePlans.map((plan) => ({
      value: plan.id,
      label: `${plan.name} - $${(plan.basePriceCents / 100).toFixed(2)}/mo (${plan.includedSeats} seats)`,
    }));
  }, [basePlans]);

  const selectedPlan = useMemo(() => {
    return basePlans.find((plan) => plan.id === selectedPlanId);
  }, [basePlans, selectedPlanId]);

  const planId = organization?.planId;

  // Initialize with current plan when modal opens, reset when closes.
  // Only pre-select if the current plan is actually present in the available
  // options; otherwise leave it empty so the select doesn't hold a hidden value
  // that would silently re-submit on confirm (false "success", no change).
  useEffect(() => {
    if (isOpen) {
      const currentIsSelectable = basePlans.some(
        (plan) => plan.id === planId,
      );
      setSelectedPlanId(currentIsSelectable && planId ? planId : '');
    } else {
      setSelectedPlanId('');
      setBillingPeriod('MONTHLY');
    }
  }, [isOpen, planId, basePlans]);

  const handleConfirm = () => {
    if (selectedPlanId) {
      onAssign(selectedPlanId, billingPeriod);
    }
  };

  const isValid = selectedPlanId !== '';
  const hasNmi = !!organization?.hasNmiSubscription;
  const isTrialExpired =
    organization?.subscriptionStatus ===
    OrganizationSubscriptionStatus.TRIAL_EXPIRED;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading || isLoadingPlans}
      handleConfirm={handleConfirm}
      title={t.assignPlanTitle}
      confirmText={t.assignConfirm}
      disableConfirm={!isValid}>
      <div className="flex flex-col gap-6">
        {organization && (
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t.organization}
            </p>
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              {organization.name}
            </p>
            {organization.planName && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t.currentPlan} <span className="font-medium">{organization.planName}</span>
              </p>
            )}
            {organization.subscriptionStatus && (
              <div className="mt-2 flex flex-col gap-1">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {t.currentStatus}
                </span>
                <SubscriptionStatusBadge
                  status={organization.subscriptionStatus}
                  trialEndsAt={organization.trialEndsAt}
                  trialExpiredAt={organization.trialExpiredAt}
                />
              </div>
            )}
          </div>
        )}

        <FormGroup htmlFor="plan" label={t.selectPlanLabel}>
          <ReactSelect
            value={planOptions.find((opt) => opt.value === selectedPlanId) || null}
            onChange={(option) => {
              const selected = option as SelectOption | null;
              setSelectedPlanId(selected?.value?.toString() || '');
            }}
            options={planOptions}
            placeholder={t.selectPlanPlaceholder}
            isClearable={false}
            isSearchable={false}
          />
        </FormGroup>

        <FormGroup htmlFor="billingPeriod" label={t.billingPeriodLabel}>
          <ReactSelect
            value={billingPeriodOptions.find((opt) => opt.value === billingPeriod) || null}
            onChange={(option) => {
              const selected = option as SelectOption | null;
              setBillingPeriod(selected?.value?.toString() || 'MONTHLY');
            }}
            options={billingPeriodOptions}
            isClearable={false}
            isSearchable={false}
          />
        </FormGroup>

        {selectedPlan && (
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              {t.planDetails}
            </p>
            <div className="mt-2 space-y-1 text-sm text-blue-800 dark:text-blue-200">
              <p>{t.planPrice.replace('{price}', `$${(selectedPlan.basePriceCents / 100).toFixed(2)}`)}</p>
              <p>{t.planIncludedSeats.replace('{n}', String(selectedPlan.includedSeats))}</p>
              {billingPeriod === 'ANNUAL' && (
                <p className="text-green-600 dark:text-green-400">
                  {t.planAnnualTotal.replace('{total}', `$${((selectedPlan.basePriceCents * 12) / 100).toFixed(2)}`)}
                </p>
              )}
            </div>
          </div>
        )}

        {hasNmi ? (
          <div
            role="alert"
            className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
            <p className="text-sm text-red-800 dark:text-red-200">
              {t.assignNoteNmi}
            </p>
          </div>
        ) : (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              {isTrialExpired ? t.assignNoteTrialExpired : t.assignNoteDefault}
            </p>
            {isTrialExpired && organization && onExtendFreePeriod && (
              <button
                type="button"
                className="mt-2 text-sm font-medium text-teal-600 hover:underline"
                onClick={() => onExtendFreePeriod(organization)}>
                {t.extendFreePeriod}
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AssignPlanModal;
